import { useState, useEffect } from 'react';
import { Select, withStyles } from 'vopay';
import { Button, CircleButton } from 'UI';
import { useTranslation } from 'react-i18next';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useToast } from '@/providers/Toast.provider';
import useDataFetchIndicator from '@/hooks/useDataFetchIndicator';
import type { UserRoles } from 'vopay';
import useFetchApprovalUsers from '@/hooks/useApi/useFetchApprovalUsers';
import usePostCreateUserRole from '@/hooks/useApi/usePostCreateUserRole';
import usePostDeleteUserRole from '@/hooks/useApi/usePostDeleteUserRole';
import styleSheet from './SuperAdminEdit.module.scss';

const cn = withStyles(styleSheet);

interface SuperAdminEditProps {
  superAdmins: UserRoles[];
  onCancel: () => void;
  onSave: () => Promise<void>;
}

const SuperAdminEdit = ({ superAdmins, onCancel, onSave }: SuperAdminEditProps) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'TransactionApproval.Configuration.Roles.SuperAdmin.Edit',
  });
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const [, setLoading] = useDataFetchIndicator();
  const { response: allUsers, fetchApprovalUsers } = useFetchApprovalUsers();
  const [, , createUserRole] = usePostCreateUserRole({});
  const [, , deleteUserRole] = usePostDeleteUserRole({});

  const [currentAdmins, setCurrentAdmins] = useState<string[]>(superAdmins.map((admin) => admin.Username));

  useEffect(() => {
    void fetchApprovalUsers();
  }, []);

  // Filter out users that are already super admins
  const getAvailableUsers = (excludeAdmins: string[]) => {
    return allUsers.filter((user) => !excludeAdmins.includes(user.Username));
  };

  const createUserOptions = (excludeAdmins: string[]) => {
    const available = getAvailableUsers(excludeAdmins);
    return available.map((user) => ({
      label: user.Username,
      value: user.Username,
    })) as any;
  };

  const handleAdminChange = (index: number, newValue: string) => {
    const newAdmins = [...currentAdmins];
    newAdmins[index] = newValue;
    setCurrentAdmins(newAdmins);
  };

  const handleRemoveAdmin = (index: number) => {
    setCurrentAdmins(currentAdmins.filter((_, i) => i !== index));
  };

  const handleAddAdmin = () => {
    setCurrentAdmins([...currentAdmins, '']);
  };

  const handleSave = async () => {
    setLoading(true);

    try {
      const originalAdmins = superAdmins.map((admin) => admin.Username);
      const validAdmins = currentAdmins.filter((admin) => admin !== '');

      // Find admins to delete (in original but not in current)
      const adminsToDelete = originalAdmins.filter((username) => !validAdmins.includes(username));

      // Find admins to create (in current but not in original)
      const adminsToCreate = validAdmins.filter((username) => !originalAdmins.includes(username));

      // Create array of promises for all operations
      const promises: Promise<any>[] = [];

      // Add delete operations to promises array
      adminsToDelete.forEach((username) => {
        promises.push(deleteUserRole({ Username: username, Role: 'super admin' }));
      });

      // Add create operations to promises array
      adminsToCreate.forEach((username) => {
        promises.push(createUserRole({ Username: username, Role: 'super admin' }));
      });

      // Execute all promises and wait for completion
      const results = await Promise.allSettled(promises);

      // Check if any operation failed (either rejected or response has Success: false)
      const failedOperations = results.filter((result) => {
        if (result.status === 'rejected') {
          return true;
        }
        if (result.status === 'fulfilled' && result.value && !result.value.Success) {
          return true;
        }
        return false;
      });

      if (failedOperations.length > 0) {
        // Show error dialog if some operations failed
        alertDialog.error(
          t('saveErrorTitle', { defaultValue: 'Error' }),
          t('saveErrorMessage', { defaultValue: 'Some settings have failed to save. Please try again.' }),
          {
            abortLabel: t('closeButton', { defaultValue: 'Close' }),
          },
        );
      } else {
        // Show success toast if all operations succeeded
        toast.success(t('saveSuccessMessage', { defaultValue: 'Super Admin changes saved' }));
      }
    } catch (error) {
      // Catch any unexpected errors
      alertDialog.error(
        t('saveErrorTitle', { defaultValue: 'Error' }),
        t('saveErrorMessage', { defaultValue: 'Some settings have failed to save. Please try again.' }),
        {
          abortLabel: t('closeButton', { defaultValue: 'Close' }),
        },
      );
    } finally {
      setLoading(false);
      onSave();
    }
  };

  return (
    <div className={cn('container')}>
      <div className={cn('instructions')}>
        {t('instructions', { defaultValue: "Transactions created by these users don't need approval." })}
      </div>

      <div className={cn('adminList')}>
        {currentAdmins.map((username, index) => {
          // Get available users excluding all current admins except the current one
          const otherAdmins = currentAdmins.filter((_, i) => i !== index);
          const userOptions = createUserOptions(otherAdmins);

          return (
            <div key={index} className={cn('adminRow')}>
              <CircleButton
                colorScheme="destructive"
                iconFill
                icon="delete"
                onClick={() => handleRemoveAdmin(index)}
                aria-label={t('removeButton', { defaultValue: 'Remove' })}
              />
              <div className={cn('selectWrapper')}>
                <Select
                  version="2"
                  name={`superAdmin-${index}`}
                  filterable
                  placeholder={t('selectPlaceholder', { defaultValue: 'Select user' })}
                  options={userOptions}
                  value={username}
                  onChange={(e) => handleAdminChange(index, e.target.value)}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className={cn('actions')}>
        <Button onClick={handleAddAdmin} size="small" iconLeft="add">
          {t('addButton', { defaultValue: 'Additional Super Admin' })}
        </Button>
        <div className={cn('actionButtons')}>
          <Button onClick={onCancel} size="small">
            {t('cancelButton', { defaultValue: 'Cancel' })}
          </Button>
          <Button onClick={handleSave} size="small" colorScheme="primary">
            {t('saveButton', { defaultValue: 'Save' })}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminEdit;
